<template>
    <Tabs
        v-bind="$attrs"
        :class="tabsClasses"
        :value="currentValue"
        @update:value="handleTabUpdate"
    >
        <TabList>
            <component
                :is="tab"
                v-for="(tab, index) in tabVNodes"
                :key="`tab-${index}`"
            />
            <div v-if="hasTabListEnd" class="e-tabs-tab-list-end">
                <slot name="tab-list-end" />
            </div>
        </TabList>
        <TabPanels>
            <component
                :is="panel"
                v-for="(panel, index) in tabPanelVNodes"
                :key="`panel-${index}`"
            />
        </TabPanels>
    </Tabs>
</template>

<script>
import Tab from 'primevue/tab';
import TabList from 'primevue/tablist';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tabs from 'primevue/tabs';
import { h } from 'vue';
/**
 * ETabs - Abstraction wrapper around PrimeVue's Tabs
 */
export default {
    name: 'ETabs',
    components: {
        Tabs,
        TabList,
        Tab,
        TabPanels,
        TabPanel,
    },
    inheritAttrs: false,
    props: {
        type: {
            type: String,
            default: undefined,
        },
        position: {
            type: String,
            default: undefined,
        },
        expanded: {
            type: Boolean,
            default: undefined,
        },
    },
    computed: {
        tabsClasses() {
            const classes = [];

            if (this.position === 'is-centered') {
                classes.push('e-tabs-centered');
            } else if (this.position === 'is-right') {
                classes.push('e-tabs-right');
            }

            if (this.expanded) {
                classes.push('e-tabs-expanded');
            }

            return classes.join(' ');
        },

        collectedVNodes() {
            const tabVNodes = [];
            const tabPanelVNodes = [];

            if (this.$slots.default) {
                const slotContent = this.$slots.default();
                if (slotContent) {
                    this.extractTabVNodes(slotContent, tabVNodes, tabPanelVNodes, 0);
                }
            }

            return { tabVNodes, tabPanelVNodes };
        },

        tabVNodes() {
            return this.collectedVNodes.tabVNodes;
        },

        tabPanelVNodes() {
            return this.collectedVNodes.tabPanelVNodes;
        },

        hasTabListEnd() {
            return Boolean(this.$slots['tab-list-end']);
        },

        currentValue() {
            // Handle default value if not provided
            let defaultValue = '0';
            if (this.tabVNodes.length > 0 && this.tabVNodes[0].props && this.tabVNodes[0].props.value !== undefined) {
                defaultValue = String(this.tabVNodes[0].props.value);
            }

            // Convert value to string to ensure consistency between Buefy (numbers) and PrimeVue (strings)
            return String(this.$attrs.value ?? this.$attrs.modelValue ?? defaultValue);
        },
    },

    emits: ['update:modelValue', 'input'],

    methods: {
        handleTabUpdate(newValue) {
            // Convert string value back to number for compatibility with Buefy's numeric indices if it's purely digits
            const numericValue = /^\d+$/.test(newValue) ? Number(newValue) : newValue;

            this.$emit('update:modelValue', numericValue);
            this.$emit('input', numericValue);
        },

        extractTabVNodes(vnodes, tabVNodes, tabPanelVNodes, startIndex) {
            if (!vnodes || !Array.isArray(vnodes)) {
                return startIndex;
            }

            let tabIndex = startIndex;

            vnodes.forEach(vnode => {
                if (this.isETabItem(vnode)) {
                    const { tabVNode, tabPanelVNode } = this.createTabAndPanelFromETabItem(vnode, tabIndex);
                    if (tabVNode && tabPanelVNode) {
                        tabVNodes.push(tabVNode);
                        tabPanelVNodes.push(tabPanelVNode);
                        tabIndex++;
                    }
                } else if (vnode.children) {
                    if (Array.isArray(vnode.children)) {
                        tabIndex = this.extractTabVNodes(vnode.children, tabVNodes, tabPanelVNodes, tabIndex);
                    } else if (typeof vnode.children === 'object') {
                        Object.values(vnode.children).forEach(childSlot => {
                            if (typeof childSlot === 'function') {
                                tabIndex = this.extractTabVNodes(childSlot(), tabVNodes, tabPanelVNodes, tabIndex);
                            } else if (Array.isArray(childSlot)) {
                                tabIndex = this.extractTabVNodes(childSlot, tabVNodes, tabPanelVNodes, tabIndex);
                            }
                        });
                    }
                }
            });

            return tabIndex;
        },

        isETabItem(vnode) {
            const { type } = vnode;
            const name = type?.name || type?.displayName;
            // Check for both the component name and the likely globally registered name
            return (
                name === 'ETabItem' ||
                vnode.type === 'e-tab-item' ||
                (typeof vnode.type === 'string' && vnode.type.includes('ETabItem'))
            );
        },

        createTabAndPanelFromETabItem(etabItemVNode, tabIndex) {
            const props = etabItemVNode.props || {};

            if (props.visible === false) {
                return { tabVNode: null, tabPanelVNode: null };
            }

            const valueId = String(props.value !== undefined ? props.value : tabIndex);
            const tabContent = props.label;

            // Build Tab VNode (header)
            const tabSlots = {};
            if (etabItemVNode.children && etabItemVNode.children.header) {
                tabSlots.default = () => etabItemVNode.children.header();
            } else if (props.icon) {
                tabSlots.default = () => [h('i', { class: `uil uil-${props.icon} mr-2` }), h('span', {}, tabContent)];
            } else if (tabContent) {
                tabSlots.default = () => tabContent;
            }

            const tabVNode = h(
                Tab,
                {
                    value: valueId,
                    disabled: props.disabled === true || props.disabled === '',
                    pt: {
                        root: ({ context }) => ({
                            style: {
                                color: context.active ? 'var(--primary-color)' : 'var(--text-color)',
                                fontWeight: 'normal',
                            },
                            onMouseenter: e => {
                                if (!context.active) {
                                    e.currentTarget.style.color = 'var(--primary-color)';
                                }
                            },
                            onMouseleave: e => {
                                if (!context.active) {
                                    e.currentTarget.style.color = 'var(--text-color)';
                                }
                            },
                        }),
                    },
                },
                tabSlots
            );

            // Build TabPanel VNode (content)
            const tabPanelSlots = {};
            if (etabItemVNode.children && etabItemVNode.children.default) {
                tabPanelSlots.default = () => etabItemVNode.children.default();
            }

            const tabPanelVNode = h(
                TabPanel,
                {
                    value: valueId,
                },
                tabPanelSlots
            );

            return { tabVNode, tabPanelVNode };
        },
    },
};
</script>

<style scoped lang="scss">
@import "~assets/scss/toolbox.scss";

/* Center aligned tabs */
.e-tabs-centered {
    :deep(.p-tablist-tab-list) {
        justify-content: center;
    }
}

/* Right aligned tabs */
.e-tabs-right {
    :deep(.p-tablist-tab-list) {
        justify-content: flex-end;
    }
}

/* Expanded/full width tabs */
.e-tabs-expanded {
    :deep(.p-tablist-tab-list) {
        display: flex;

        .p-tab {
            flex: 1;
        }
    }
}

/* Trailing actions in the tab strip (p-tablist-tab-list). */
.e-tabs--tab-list-end {
    :deep(.p-tablist-tab-list) {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.5rem;
    }

    .e-tabs-tab-list-end {
        margin-left: auto;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }
}
</style>
